/**
 * Product pages (/products/:slug). Edit copy here.
 * Photocil content is summarised from www.photociltherapy.com; Hair Enzyme Booster content uses facts supplied by SPC.
 * Never use the full legal name; refer to the company only as "SPC".
 */

export const products = {
  photocil: {
    slug: "photocil",
    name: "Photocil",
    meta: {
      title: "Photocil: sun-powered phototherapy cream | SPC",
      description:
        "Photocil is a patented topical cream that filters sunlight so only therapeutic NB-UVB reaches the skin. Marketed in India as Photofirst. Licensing and distribution through SPC.",
    },
    hero: {
      eyebrow: "Dermatology · Phototherapy",
      lines: ["Photocil.", "Phototherapy,", "powered by *sunlight*."],
      body:
        "A patented topical cream that filters natural sunlight so that only therapeutic NB-UVB reaches the skin, bringing phototherapy for vitiligo and psoriasis out of the clinic and into the patient's daily routine.",
      image: "/images/sunlight-hand.webp",
    },
    facts: [
      { label: "Indications", value: "Vitiligo, psoriasis, atopic dermatitis, chronic pruritus" },
      { label: "Routine", value: "About 30 minutes in sunlight, 3 times a week" },
      { label: "Typical plan", value: "12 weeks" },
      { label: "India brand", value: "Photofirst, CDSCO approved, November 2022" },
      { label: "Intellectual property", value: "US Patent No. 10,111,821" },
    ],
    sections: [
      {
        id: "what",
        eyebrow: "The product",
        title: "Clinic phototherapy, without the clinic.",
        paragraphs: [
          "Phototherapy is an established standard of care for psoriasis and vitiligo, but it has traditionally needed a UV lamp, usually in a clinic, several times a week.",
          "Photocil acts as a precision filter on the skin. It blocks non-therapeutic solar radiation and lets the therapeutic NB-UVB wavelengths through, so patients receive phototherapy from natural sunlight.",
          "It is steroid free, needs no oral medication or injections, and can be used on its own or alongside existing treatments. It stays on the skin surface and is not absorbed into the body.",
        ],
      },
      {
        id: "how",
        eyebrow: "Mechanism",
        title: "A band-pass filter on the skin.",
        paragraphs: [
          "Two photostable UV-absorbing molecules, alpha-glucosyl hesperidin and DHHB, form a band-pass filter on the skin surface. Erythema-causing wavelengths are blocked while the therapeutic NB-UVB band is transmitted, similar to the natural filtering of sunlight at the Dead Sea.",
        ],
        list: [
          "Transmits: 311 to 313 nm NB-UVB",
          "Blocks: wavelengths below 300 nm",
          "Actives: alpha-glucosyl hesperidin and DHHB",
          "Acts on the skin surface only",
        ],
      },
      {
        id: "use",
        eyebrow: "Protocol",
        title: "How a session works.",
        ordered: true,
        list: [
          "Choose a sunny time, ideally close to midday, to keep the session short.",
          "Apply the cream generously to the affected skin.",
          "Cover unaffected skin with clothing, a hat and SPF 30+ sunscreen.",
          "Go outside and start a timed session with the Photocil session tool or app.",
          "End the session when notified and go back indoors.",
        ],
      },
      {
        id: "regulatory",
        eyebrow: "Regulatory status",
        title: "Approved and registered.",
        list: [
          "India: CDSCO approved, marketed as Photofirst",
          "Latin America: registered as a cosmetic",
          "United States: US Patent No. 10,111,821. Not available over the counter in the US.",
        ],
      },
    ],
    evidence: {
      title: "Published evidence.",
      note: "Summary of published and submitted studies. Outcomes vary by patient.",
      studies: [
        {
          title: "Topical cream delivers NB-UVB from sunlight for the treatment of vitiligo",
          source: "Wang X, McCoy J, Lotti T, Goren A. Expert Opinion on Pharmacotherapy, 2014;15(18):2623-2627",
          finding: "Describes the mechanism and clinical use of the cream for vitiligo.",
        },
        {
          title: "Novel topical cream delivers safe and effective sunlight therapy for vitiligo by selectively filtering damaging ultraviolet radiation",
          source: "Goren A, Salafia A, McCoy J, Keene S, Lotti T. Dermatologic Therapy, 2014",
          finding: "100% response rate after an average of 11 weeks at 3 sessions a week. 28% of patients reached 70% or more repigmentation. No serious adverse events.",
        },
        {
          title: "Novel topical cream delivers safe and effective alternative to traditional psoriasis phototherapy",
          source: "Goren A, Salafia A, McCoy J, Keene S, Lotti T, Petrusevska A. Dermatologic Therapy, 2014;27:260-263",
          finding: "43% complete clearance and 75% mean lesion surface area clearance.",
        },
        {
          title: "In vitro evaluation of a novel topical cream for vitiligo and psoriasis that selectively delivers NB-UVB therapy when exposed to sunlight",
          source: "McCoy J, Goren A, Lotti T. Dermatologic Therapy, 2014;27:117-120",
          finding: "Laboratory characterisation of the band-pass filter formulation.",
        },
        {
          title: "Management of chronic pruritus with a UV filtering topical cream",
          source: "Zanardelli M, Kovacevic M, McCoy J, Wang X, Goren A, Lotti T. Dermatologic Therapy, 2016;29:101-103",
          finding: "All 7 patients with chronic pruritus noted improvement after 3 months.",
        },
        {
          title: "Topical band-pass filter cream assisted home-based NB-UVB",
          source: "Sonthalia S. Journal of Cosmetic Dermatology, 2021;20(7):2141-2147",
          finding: "Review of home-based NB-UVB with a topical band-pass filter cream as an alternative to clinic phototherapy.",
        },
        {
          title: "Multicenter prospective real-world study of Photofirst in Indian dermatology centres",
          source: "Indian Dermatology Online Journal, under peer review",
          finding: "First India-specific real-world evidence for the product, once published.",
        },
      ],
    },
    availability: [
      "SPC is the licensed partner of Caring Brands for Photocil, and has licensed exclusive India rights to Eris Oaknet, which markets it as Photofirst.",
      "Photocil is active in India, Mexico, Brazil, Colombia, Ecuador, Peru, Dominican Republic, UAE, Saudi Arabia, Egypt, Korea, Philippines, Indonesia, Thailand, Vietnam and Kazakhstan.",
      "SPC also built the Photofirst Companion App, now in 11 Indian languages, to support patients on home phototherapy. We welcome enquiries from distributors and licensees in further markets.",
    ],
    links: [
      { label: "Photocil product site", href: "https://www.photociltherapy.com" },
      { label: "Photofirst Companion App", href: "https://photofirstcompanion.com" },
      {
        label: "India launch news",
        href: "https://www.accessnewswire.com/newsroom/en/healthcare-and-pharmaceutical/eris-oaknet-healthcare-and-cosmofix-technovation-announce-market-launc-726352",
      },
    ],
    cta: "Interested in Photocil for your market? Talk to SPC.",
  },

  "minoxidil-booster": {
    slug: "minoxidil-booster",
    name: "Hair Enzyme Booster",
    meta: {
      title: "Hair Enzyme Booster: a minoxidil booster | SPC",
      description:
        "The Hair Enzyme Booster supports the follicle enzyme that activates minoxidil. Licensed by SPC to Glenmark, Dr. Reddy's, Cipla, Cosmofix, Eris and Dabur.",
    },
    hero: {
      eyebrow: "Hair science",
      lines: ["Hair Enzyme", "*Booster*."],
      body: "A minoxidil booster that supports the follicle enzyme that activates minoxidil, for the many users who do not respond to minoxidil alone.",
      image: "/images/hair-serum.webp",
    },
    facts: [
      { label: "The problem", value: "Around 40% of minoxidil users do not respond to treatment" },
      { label: "Approach", value: "Supports the follicle enzyme that activates minoxidil" },
      { label: "Licensees", value: "Glenmark, Dr. Reddy's, Cipla, Cosmofix, Eris, Dabur" },
      { label: "In market", value: "Several brands, including Dr. Reddy's Mintop Booster" },
    ],
    sections: [
      {
        id: "what",
        eyebrow: "The product",
        title: "Helping minoxidil work for more people.",
        paragraphs: [
          "Minoxidil has to be activated by an enzyme in the hair follicle, and around 40% of users do not respond to treatment. The Hair Enzyme Booster addresses the enzyme behind this, supporting the activation of minoxidil.",
        ],
      },
      {
        id: "model",
        eyebrow: "Commercial model",
        title: "One technology, several leading brands.",
        paragraphs: [
          "SPC structured multiple non-exclusive licensing agreements so each partner can launch the booster under its own brand, with finished product supplied from India.",
        ],
      },
    ],
    evidence: {
      eyebrow: "Evidence",
      title: "Evidence",
      statement: {
        text: "SPC ran the product's clinical, safety and stability programme in India.",
        label: "See R&D and CRO Services",
        to: "/rd-cro-services",
      },
    },
    availability: [
      "SPC is the licensed partner of Caring Brands for the Hair Enzyme Booster. SPC has licensed it to Glenmark, Dr. Reddy's, Cipla, Cosmofix, Eris and Dabur, and works with partners on registrations in Australia, the Philippines, Russia and Latin America. We are looking for dermatology, trichology and consumer health partners in further markets.",
    ],
    links: [
      { label: "See it in market", href: "https://jupiter.1mg.com/otc/mintop-booster-solution-otc971902" },
      {
        label: "MinoxiBoost India launch news",
        href: "https://www.tipranks.com/news/press-releases/jupiter-wellness-and-sanpellegrino-cosmetics-announce-launch-of-cosmofix-minoxiboost-in-india-boosts-effectiveness-of-minoxidil-rogaine-for-hair-growth",
      },
    ],
    cta: "Interested in the Hair Enzyme Booster for your market? Talk to SPC.",
  },
};

export const productLinks = Object.values(products).map((p) => ({ label: p.name, to: `/products/${p.slug}` }));
